import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatLocalISODate } from '@/lib/dateUtils';
import { authedPost } from '@/lib/api';
import type { ProjectResponse } from '@releve/shared';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';

export interface CreateProjectData {
  name: string;
  description?: string;
  status?: string;
  start_date?: Date | string | null;
  end_date?: Date | string | null;
  budget?: number | null;
  project_type_id?: string;
  artistic_director_id?: string | null;
  sustainability_score?: number;
  carbon_footprint_level?: string;
  local_sourcing_percentage?: number;
  sustainability_notes?: string;
}

export function useCreateProject() {
  const { user } = useAuth();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  // Format date for database (DATE type expects YYYY-MM-DD format)
  const formatDateForDB = (date: Date | string | null | undefined) => {
    if (!date) return null;

    if (typeof date === 'string') {
      // If it's already a string, assume it's in correct format
      return date;
    }

    // If it's a Date object, take its LOCAL calendar day — the picker hands
    // back local-midnight Dates, and toISOString would shift them a day early
    // for any UTC+ user (docs/DATES.md).
    return formatLocalISODate(date);
  };

  const mutation = useMutation({
    mutationFn: async (data: CreateProjectData) => {
      if (!user) {
        toast.error('You must be logged in to create a project');
        throw new Error('Not authenticated');
      }

      if (!selectedOrganization) {
        toast.error('No organization selected');
        throw new Error('No organization selected');
      }

      if (!data.name?.trim()) {
        toast.error('Project name is required');
        throw new Error('Project name is required');
      }

      // The server owns `organization_id` (route param), `created_by` (the
      // verified token) and `code` (generated, AAA######, collision-retried);
      // the strict create schema 400s if any of them — or the retired
      // `team_size` — is sent. Numbers use `??` so a legitimate 0 survives.
      const projectData = {
        name: data.name.trim(),
        description: data.description || null,
        status: data.status || 'planning',
        start_date: formatDateForDB(data.start_date),
        end_date: formatDateForDB(data.end_date),
        budget: data.budget ?? null,
        project_type_id: data.project_type_id,
        artistic_director_id: data.artistic_director_id || null,
        sustainability_score: data.sustainability_score ?? null,
        carbon_footprint_level: data.carbon_footprint_level || null,
        local_sourcing_percentage: data.local_sourcing_percentage ?? null,
        sustainability_notes: data.sustainability_notes || null,
      };

      let createdProject: ProjectResponse['project'];
      try {
        const result = await authedPost<ProjectResponse>(
          `/api/v1/projects/orgs/${selectedOrganization.id}/projects`,
          projectData,
        );
        createdProject = result.project;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        toast.error('Failed to create project: ' + message);
        throw error;
      }

      return createdProject;
    },
    onSuccess: () => {
      toast.success('Project created successfully!');
      // Invalidate all projects queries for the current organization
      queryClient.invalidateQueries({ queryKey: ['projects', selectedOrganization?.id] });
    },
    onError: (error) => {
      console.error('Error creating project:', error);
    },
  });

  return {
    createProject: mutation.mutateAsync,
    isLoading: mutation.isPending,
  };
}